import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ME_QUERY_KEY } from '@/hooks/useAuth';
import { api, extractApiError } from '@/lib/api';
import { setAccessToken } from '@/lib/auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

const signInSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Required'),
});

type SignInValues = z.infer<typeof signInSchema>;

// Backend LoginResponse is flat — see backend/app/schemas/auth.py.
interface LoginResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_at: string;
  // user/org/quota also returned but we don't consume them here — useAuth
  // refetches /me after token is set.
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // No local auth-guard here — PublicLayout redirects authed users to /dashboard
  // before this component mounts. Duplicate guards introduce a flash-of-form
  // during the initial ~200ms when isLoading is true.

  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: async (values: SignInValues) => {
      const resp = await api.post<LoginResponse>('/auth/login', values);
      return resp.data;
    },
    onSuccess: async (data) => {
      setAccessToken(data.access_token);
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
      navigate(from || '/dashboard', { replace: true });
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'auth.invalid_credentials') {
        toast.error('Invalid email or password');
      } else if (apiErr?.code === 'auth.rate_limited') {
        toast.error('Too many attempts. Please try again later.');
      } else {
        toast.error(apiErr?.message || 'Sign-in failed. Please try again.');
      }
    },
  });

  const onSubmit = (values: SignInValues) => {
    mutation.mutate(values);
  };

  const onInvalid = (errors: Record<string, { message?: string }>) => {
    const first = Object.values(errors).find((e) => e?.message);
    toast.error(first?.message || 'Please fix the highlighted fields.');
  };

  const isPending = mutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Welcome back</CardTitle>
        <CardDescription>Sign in to your DAGsight account.</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="current-password"
                      placeholder="********"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? 'Signing in…' : 'Sign in'}
            </Button>
            <div className="flex w-full items-center justify-between text-sm text-muted-foreground">
              <Link to="/forgot-password" className="hover:underline">
                Forgot password?
              </Link>
              <Link to="/sign-up" className="hover:underline">
                Create account
              </Link>
            </div>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
